import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  MessageCircle,
  Globe,
  Linkedin,
  Twitter,
  CheckCircle,
  ChevronDown
} from 'lucide-react';
import { dbHelpers } from '../config/database';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone:'',
    company: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeFaq, setActiveFaq] = useState<number | null>(0);

  const faqs = [
    {
      q: 'Do you sign non-disclosure agreements (NDAs)?',
      a: 'Absolutely. We respect corporate IP confidentiality. We execute mutual NDAs before reviewing any system blueprints or architecture details.'
    },
    {
      q: 'How quickly can we kick off development?',
      a: 'Once scope documents and budgets are signed, we assign dedicated developers and launch sprint blueprints within 5 to 7 business days.'
    },
    {
      q: 'What engagement models do you offer?',
      a: 'We support fixed-bid projects (best for defined scope), monthly managed developers, and agile sprints with bi-weekly updates.'
    },
    {
      q: 'Do you provide SLA post-launch support?',
      a: 'Yes, we provide 24/7 cloud server monitoring, security patching audits, and continuous scaling support under monthly SLA retainers.'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await dbHelpers.createInquiry({ 
        name: formData.name, 
        email: formData.email,   
        phone: formData.phone,
        company: formData.company,  
        subject: formData.subject, 
        message: formData.message 
      });

      setSubmitStatus('success');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });

    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneClick = (phone: string) => {
    window.open(`tel:${phone.replace(/\s/g, '')}`, '_self');
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}?subject=Inquiry from Website&body=Hello X2InfoTech Team,%0D%0A%0D%0AI am interested in your services. Please contact me.%0D%0A%0D%0AThank you.`, '_self');
  };

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Phone',
      details: ['+91 90741 40432'],
      color: 'text-brand-primary',
      clickable: true,
      type: 'phone'
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email',
      details: ['info@x2infotech.in', 'hr@x2infotech.in'],
      color: 'text-brand-primary',
      clickable: true,
      type: 'email'
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Address',
      details: ['SAINIK COLONY, PINTO PARK, GOLA KA MANDIR', 'GWALIOR, MADHYA PRADESH, INDIA, 474005'],
      color: 'text-brand-primary',
      clickable: false
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Business Hours',
      details: ['Mon - Fri: 9:00 AM - 6:00 PM', 'Sat: 10:00 AM - 2:00 PM'],
      color: 'text-brand-primary',
      clickable: false
    }
  ];

  return (
    <section id="contact" className="py-20 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Connect With Us</span>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-secondary">
            Get In <span className="text-brand-primary">Touch</span>
          </h2>
          <p className="text-gray-600 text-lg">
            Ready to scale your systems? Drop us a line below and let's structure a roadmap proposal together.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column: Contact info & FAQs */}
          <div className="space-y-8 animate-fade-in-left">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-brand-secondary">
                Let's Start a Conversation
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm">
                Whether you need custom software development, cloud migration, or strategic IT consulting, our team is here to help. Reach out to us through any of the channels below.
              </p>
            </div>

            {/* Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => (
                <div 
                  key={index} 
                  className="bg-gray-50 p-5 rounded-2xl border-l-4 border-brand-primary hover:shadow-lg transition-all duration-355 transform hover:scale-[1.02] group"
                >
                  <div className={`${info.color} mb-3 group-hover:animate-bounce`}>
                    {info.icon}
                  </div>
                  <h4 className="font-bold text-sm text-brand-secondary mb-1">
                    {info.title}
                  </h4>
                  {info.details.map((detail, detailIndex) => (
                    <div key={detailIndex}>
                      {info.clickable ? (
                        <button
                          onClick={() => {
                            if (info.type === 'phone') {
                              handlePhoneClick(detail);
                            } else if (info.type === 'email') {
                              handleEmailClick(detail);
                            }
                          }}
                          className="text-gray-600 text-xs hover:text-brand-primary transition-colors duration-200 hover:underline block text-left"
                        >
                          {detail}
                        </button>
                      ) : (
                        <span className="text-gray-600 text-xs block">{detail}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Interactive FAQs Accordion */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h4 className="font-bold text-brand-secondary text-base">Frequently Asked Questions</h4>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <div 
                    key={idx}
                    className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
                      activeFaq === idx ? 'border-brand-primary shadow-md' : 'border-gray-200'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                      className="w-full p-4 flex justify-between items-center text-left focus:outline-none"
                    >
                      <span className="font-bold text-xs text-brand-secondary">{faq.q}</span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                        activeFaq === idx ? 'transform rotate-180' : ''
                      }`} />
                    </button>
                    <div className={`transition-all duration-250 ${
                      activeFaq === idx ? 'max-h-32 border-t border-gray-100 p-4 bg-gray-50/50' : 'max-h-0 overflow-hidden'
                    }`}>
                      <p className="text-gray-600 text-xs leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200 animate-fade-in-right flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-brand-secondary mb-6 border-b border-gray-200 pb-3">Submit Project Inquiry</h3>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center animate-fade-in">
                  <span className="text-green-800 text-xs font-semibold">Message logged successfully! We'll reply within 24 hours.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in">
                  <span className="text-red-800 text-xs font-semibold font-mono">Error registering submission. Please try again.</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="block text-xs font-bold text-brand-secondary">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white hover:border-brand-primary focus:outline-none"
                      placeholder="Enter your name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="block text-xs font-bold text-brand-secondary">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white hover:border-brand-primary focus:outline-none"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="company" className="block text-xs font-bold text-brand-secondary">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white hover:border-brand-primary focus:outline-none"
                      placeholder="Company name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="block text-xs font-bold text-brand-secondary">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white hover:border-brand-primary focus:outline-none"
                      placeholder="Enter mobile number"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="subject" className="block text-xs font-bold text-brand-secondary">
                    Inquiry Focus *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white hover:border-brand-primary focus:outline-none"
                  >
                    <option value="">Select a subject</option>
                    <option value="custom-development">Custom Development</option>
                    <option value="cloud-services">Cloud Services</option>
                    <option value="mobile-apps">Mobile Apps</option>
                    <option value="consulting">IT Consulting</option>
                    <option value="support">Technical Support</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="message" className="block text-xs font-bold text-brand-secondary">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all duration-200 bg-white resize-none hover:border-brand-primary focus:outline-none"
                    placeholder="Tell us about your project requirements..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-4 bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white py-3.5 px-6 rounded-xl font-bold shadow-lg hover:shadow-[0_8px_25px_rgba(20,201,203,0.35)] transition-all duration-200 flex items-center justify-center group disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.01] active:scale-[0.99]"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending inquiry...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Contact;